import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import Logo from "../Image/Logo.jpg";
import WarehouseScene from "../components/common/WarehouseScene";
import WarehouseBackdrop from "../components/common/WarehouseBackdrop";

interface LoginError {
  message: string;
  field?: string;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);

  // Animation states
  const [showSplash, setShowSplash] = useState(true);
  const [logoVisible, setLogoVisible] = useState(false);
  const [logoSlideLeft, setLogoSlideLeft] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [loginContainerVisible, setLoginContainerVisible] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();
  // Below lg there is no left brand panel, so the splash logo must not slide toward it
  const isMobile = useIsMobile();

  useEffect(() => {
    const sequence = async () => {
      // Step 1: Show logo with smooth pop-up animation
      setTimeout(() => setLogoVisible(true), 400);

      // Step 2: Hold logo in center for visual impact
      setTimeout(() => {
        setLogoSlideLeft(true);
      }, 2000);

      // Step 3: Begin final layout fade-in while logo is moving
      setTimeout(() => {
        setAnimationComplete(true);
      }, 2600);

      // Step 4: Hide splash screen after logo reaches final position
      setTimeout(() => {
        setShowSplash(false);
      }, 2900);

      // Step 5: Start login container slide animation after splash is hidden
      setTimeout(() => {
        setLoginContainerVisible(true);
      }, 3100);
    };

    sequence();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const result = await login(email, password);

      if (result.requiresOtp && result.otpSessionId) {
        sessionStorage.setItem('otpSessionId', result.otpSessionId);
        sessionStorage.setItem('otpEmail', result.email ?? email);
        navigate('/verify-otp');
        return;
      }

      navigate("/dashboard");
    } catch (err: any) {
      console.error('Login error:', err);

      // The axios interceptor returns error object directly with message property
      // Handle validation errors with errors array
      let errorMessage = 'Login failed. Please try again.';
      if (err?.errors && Array.isArray(err.errors) && err.errors.length > 0) {
        errorMessage = err.errors.join(', ');
      } else if (err?.message) {
        errorMessage = err.message;
      }

      // Determine field based on status code for UI highlighting
      let field: string | undefined;
      if (err?.statusCode === 401 || err?.statusCode === 404) {
        field = 'credentials';
      } else if (err?.statusCode === 400) {
        field = 'email'; // Validation errors typically relate to email format
      }

      setError({
        message: errorMessage,
        field
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--bg-main)' }}>
      {/* Splash Screen */}
      {showSplash && (
        <div className={`
          fixed inset-0 z-50 login-layout-container
          transition-all duration-500 ease-in-out
          ${logoSlideLeft ? '' : ''}
        `} style={{
          background: 'linear-gradient(145deg, #111827 0%, #0f2036 48%, #1e3a5f 100%)',
          opacity: showSplash ? 1 : 0,
          visibility: showSplash ? 'visible' : 'hidden',
          transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: showSplash ? 100 : -1
        }}>
          <div className="absolute inset-0 surface-grid opacity-30" />
          {/* Centered Logo Container that moves to final position */}
          <div className="splash-logo-container splash-slide-transition absolute" style={{
            willChange: 'transform, opacity',
            left: logoSlideLeft && !isMobile ? '25%' : '50%',
            top: '50%',
            transform: logoSlideLeft
              ? `translate(-50%, -50%) scale(${isMobile ? 0.85 : 1})`
              : logoVisible
                ? 'translate(-50%, -50%) scale(1)'
                : 'translate(-50%, -50%) scale(0.9)',
            transition: 'all 1000ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            zIndex: 60
          }}>
            <div className="w-40 h-40 sm:w-56 sm:h-56 lg:w-64 lg:h-64 rounded-3xl p-3 lg:p-4 shadow-panel bg-white border border-white/40">
              <img
                src={Logo}
                alt="Logo"
                className="w-full h-full rounded-2xl transition-all duration-700 ease-out object-contain"
                style={{
                  borderRadius: '16px',
                  transform: logoVisible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.95)',
                  transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Final Login Layout with enhanced timing */}
      {animationComplete && (
        <div className="min-h-screen flex" style={{
          position: showSplash ? 'fixed' : 'static',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: animationComplete ? 1 : 0,
          transform: animationComplete ? 'scale(1)' : 'scale(0.98)',
          transition: 'all 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          zIndex: showSplash ? 90 : 1
        }}>
          {/* Left Section - Brand */}
          <div className="hidden lg:flex lg:w-[42%] relative bg-brand-900 text-white overflow-hidden">
            <div className="absolute inset-0 surface-grid opacity-20" />
            <div className="absolute -right-20 top-20 w-72 h-72 rounded-full bg-accent-500/10 blur-3xl" />
            <div className="absolute left-10 bottom-10 w-56 h-56 rounded-full bg-brand-500/20 blur-3xl" />

            <div className="relative z-10 flex flex-col justify-between w-full p-10 xl:p-14">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-md bg-white p-1.5">
                  <img src={Logo} alt="Logo" className="h-full w-full object-contain" />
                </div>
                <div>
                  <p className="text-sm font-extrabold tracking-tight">Smart WMS</p>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/50 font-bold">Warehouse Console</p>
                </div>
              </div>

              <div
                className={`max-w-md transition-all duration-700 ${loginContainerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded bg-accent-500/15 border border-accent-400/30 text-accent-300 text-[11px] font-bold uppercase tracking-[0.12em] mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
                  Operations Ready
                </div>
                <h1 className="text-4xl xl:text-5xl font-extrabold leading-[1.1] mb-4">
                  Professional floor control for modern warehouses
                </h1>
                <p className="text-white/65 text-base leading-relaxed">
                  Gate entry, GRN, dispatch, inventory, and billing — one console for supervisors and operators.
                </p>

                <div className="mt-10 grid grid-cols-3 gap-3">
                  {[
                    { label: 'Inbound', value: 'GRN' },
                    { label: 'Outbound', value: 'Dispatch' },
                    { label: 'Stock', value: 'Live' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-md border border-white/10 bg-white/5 px-3 py-3">
                      <p className="text-[10px] uppercase tracking-[0.12em] text-white/45 font-bold">{item.label}</p>
                      <p className="text-sm font-extrabold mt-1">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <WarehouseScene className="w-full max-w-sm mt-2 opacity-90" />

              <p className="text-xs text-white/35">Secure multi-tenant access · Role-based controls</p>
            </div>
          </div>

          {/* Right Section - Login Form */}
          <div className="flex-1 relative overflow-hidden flex items-center justify-center p-4 sm:p-8" style={{
            background: 'linear-gradient(135deg, #eef3f8 0%, #fef3c7 45%, #eef3f8 100%)',
            opacity: loginContainerVisible ? 1 : 0,
            transform: loginContainerVisible ? 'translateY(0)' : 'translateY(100%)',
            transition: 'all 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: '0ms'
          }}>
            <WarehouseBackdrop className="absolute inset-0 w-full h-full pointer-events-none" />

            <div className="relative z-10 w-full max-w-[420px]" style={{
              opacity: loginContainerVisible ? 1 : 0,
              transform: loginContainerVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
              transition: 'all 700ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              transitionDelay: loginContainerVisible ? '200ms' : '0ms'
            }}>
              <div className="lg:hidden mb-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-white border border-ink-200 p-1.5">
                  <img src={Logo} alt="Logo" className="h-full w-full object-contain" />
                </div>
                <div>
                  <p className="text-sm font-extrabold text-ink-900">FleetWMS</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-ink-400 font-bold">Sign in</p>
                </div>
              </div>

              <div className="bg-white border border-ink-200 rounded-lg shadow-panel p-6 sm:p-8">
                <div className="mb-6">
                  <h2 className="text-2xl font-extrabold text-ink-900">Sign in</h2>
                  <p className="text-sm text-ink-500 mt-1">Use your work credentials to continue</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-start text-red-700 gap-2">
                      <i className="fas fa-circle-exclamation mt-0.5"></i>
                      <span className="text-sm font-medium">{error.message}</span>
                    </div>
                  </div>
                )}

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-extrabold uppercase tracking-[0.08em] text-ink-600 mb-1.5">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-3.5 py-2.5 border rounded-md text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all"
                      style={{
                        borderColor: error?.field === 'email' || error?.field === 'credentials' ? '#ef4444' : 'var(--border-color)',
                        background: error?.field === 'email' || error?.field === 'credentials' ? '#fef2f2' : 'white',
                      }}
                      placeholder="name@company.com"
                    />
                  </div>

                  {/* Password Field */}
                  <div>
                    <label htmlFor="password" className="block text-xs font-extrabold uppercase tracking-[0.08em] text-ink-600 mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full px-3.5 py-2.5 pr-11 border rounded-md text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all"
                        style={{
                          borderColor: error?.field === 'credentials' ? '#ef4444' : 'var(--border-color)',
                          background: error?.field === 'credentials' ? '#fef2f2' : 'white',
                        }}
                        placeholder="Enter password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Forgot Password Link */}
                  <div className="flex justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-xs font-bold text-brand-600 hover:text-accent-700 transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  {/* Login Button */}
                  <button
                    type="submit"
                    disabled={isLoading || !email || !password}
                    className="w-full text-white font-bold py-3 px-4 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed btn-modern"
                    style={{
                      background: isLoading || !email || !password ? '#98a4b5' : '#0a1628',
                    }}
                  >
                    {isLoading ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i>
                        Authenticating...
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center gap-2">
                        Enter console
                        <i className="fas fa-arrow-right text-accent-400 text-xs"></i>
                      </span>
                    )}
                  </button>
                </form>
              </div>

              <p className="text-center text-xs text-ink-400 mt-5">
                Authorized personnel only
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
